
import { z } from 'zod';
import type { Vendor, Driver, Client, CityHallAccount } from '../types';
import { isEmailInTestUsers } from '../services/testUsers';

// ============================================
// VALIDA��O DE EMAIL �NICO (CR�TICO)
// ============================================

/**
 * Normaliza email: lowercase e trim
 * SEMPRE usar esta fun��o antes de comparar ou salvar emails
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

/**
 * Verifica se email j� existe em QUALQUER tabela
 * Email deve ser �NICO em todo o sistema
 */
export function isEmailUnique(
  email: string,
  entities: {
    vendors: Vendor[];
    drivers: Driver[];
    clients: Client[];
    fiscais: CityHallAccount[];
  }
): { isUnique: boolean; existsIn?: string } {
  const normalizedEmail = normalizeEmail(email);

  // ? VERIFICAR TEST USERS PRIMEIRO (hardcoded)
  if (isEmailInTestUsers(normalizedEmail)) {
    return { isUnique: false, existsIn: 'TEST_USERS (hardcoded)' };
  }

  // Verificar vendors
  if (entities.vendors.some(v => normalizeEmail(v.email) === normalizedEmail)) {
    return { isUnique: false, existsIn: 'VENDOR' };
  }

  // Verificar drivers
  if (entities.drivers.some(d => normalizeEmail(d.email) === normalizedEmail)) {
    return { isUnique: false, existsIn: 'DRIVER' };
  }

  // Verificar clients
  if (entities.clients.some(c => normalizeEmail(c.email) === normalizedEmail)) {
    return { isUnique: false, existsIn: 'CLIENT' };
  }

  // Verificar fiscais
  if (entities.fiscais.some(f => normalizeEmail(f.email) === normalizedEmail)) {
    return { isUnique: false, existsIn: 'CITY_HALL' };
  }

  return { isUnique: true };
}

// ============================================
// SCHEMAS ZOD POR TIPO DE USU�RIO
// ============================================

export const clientSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  email: z.string().email('Email inv�lido'),
  phone: z.string().regex(/^\d{10,11}$/, 'Telefone: DDD + n�mero (10-11 d�gitos)').optional(),
  password: z.string().min(6, 'Senha deve ter no m�nimo 6 caracteres'),
});

export const vendorRegulatedSchema = z.object({
  name: z.string().min(3, 'Nome completo obrigat�rio'),
  email: z.string().email('Email inv�lido'),
  phone: z.string().regex(/^\d{10,11}$/, 'Telefone inv�lido'),
  password: z.string().min(6, 'Senha m�nimo 6 caracteres'),
  storeName: z.string().min(3, 'Nome do estabelecimento obrigat�rio'),
  regionalId: z.string().min(1, 'Regional obrigat�ria para vendedores autorizados'),
  permitNumber: z.string().min(5, 'N�mero da autoriza��o obrigat�rio'),
  cpfCnpj: z.string().regex(/^\d{11}$|^\d{14}$/, 'CPF (11 d�gitos) ou CNPJ (14 d�gitos)'),
});

export const vendorUnregulatedSchema = z.object({
  name: z.string().min(3, 'Nome completo obrigat�rio'),
  email: z.string().email('Email inv�lido'),
  phone: z.string().regex(/^\d{10,11}$/, 'Telefone inv�lido'),
  password: z.string().min(6, 'Senha m�nimo 6 caracteres'),
  storeName: z.string().min(3, 'Nome do estabelecimento obrigat�rio'),
  // N�O solicita regionalId, permitNumber, cpfCnpj
});

export const driverSchema = z.object({
  name: z.string().min(3, 'Nome completo obrigat�rio'),
  email: z.string().email('Email inv�lido'),
  phone: z.string().regex(/^\d{10,11}$/, 'Telefone inv�lido'),
  password: z.string().min(6, 'Senha m�nimo 6 caracteres'),
  cpf: z.string().regex(/^\d{11}$/, 'CPF deve ter 11 d�gitos'),
  cnh: z.string().min(5, 'N�mero da CNH obrigat�rio'),
  vehicleType: z.enum(['motorcycle', 'car', 'bicycle', 'on_foot']),
  plate: z.string().regex(/^[A-Z]{3}-\d{4}$/, 'Placa formato ABC-1234'),
});

export const cityHallSchema = z.object({
  name: z.string().min(3, 'Nome completo obrigat�rio'),
  email: z.string().email('Email institucional obrigat�rio'),
  password: z.string().min(8, 'Senha institucional m�nimo 8 caracteres'),
  employeeId: z.string().min(3, 'Matr�cula do servidor obrigat�ria'),
  regionalId: z.string().min(1, 'Regional de atua��o obrigat�ria'),
  department: z.string().optional(),
  fiscalRole: z.string().optional(),
});

// ============================================
// VALIDA��O LEGACY (manter compatibilidade)
// ============================================

export const orderSchema = z.object({
  customerName: z.string().min(3, 'O nome deve ter pelo menos 3 caracteres'),
  customerPhone: z.string().regex(/^\d{10,11}$/, 'Telefone deve conter DDD + n�mero (10 ou 11 d�gitos)'),
  items: z.array(z.object({
    id: z.string(),
    name: z.object({
        pt: z.string()
    }),
    quantity: z.number().min(1, 'Quantidade m�nima � 1'),
    price: z.number().positive('Pre�o deve ser positivo')
  })).min(1, 'O carrinho n�o pode estar vazio'),
  deliveryNote: z.string().min(5, 'Informe uma refer�ncia de localiza��o clara'),
  paymentMethod: z.enum(['pix', 'Cart�o na entrega', 'PIX', 'CART�O NA ENTREGA'])
});

export type OrderValidationInput = z.infer<typeof orderSchema>;

export const validateOrder = (data: any) => {
  try {
    const validatedData = orderSchema.parse(data);
    return { isValid: true, data: validatedData, errors: [] };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { 
        isValid: false, 
        data: null, 
        // Access 'issues' property of ZodError as 'errors' is not always available on the type ZodError<unknown>
        errors: error.issues.map(err => ({
            path: err.path.join('.'),
            message: err.message
        }))
      };
    }
    return { isValid: false, data: null, errors: [{ path: 'unknown', message: 'Erro inesperado na valida��o' }] };
  }
};
